import { APP_CONFIG } from "./config.ts";
import { LEGACY_MAX_ATTEMPTS } from "../shared/game-rules.ts";

export type PuzzleStatus = "ready" | "playing" | "solved" | "failed";

export interface PuzzleProgress {
  date: string;
  status: PuzzleStatus;
  attempts: string[];
  maxAttempts?: number;
  startedAt?: number;
  completedAt?: number;
  solveTimeMs?: number;
  countsForStreak: boolean;
  timingVersion?: 2;
  ranked?: boolean;
  runId?: string;
  leaderboardName?: string;
  leaderboardAutoJoinSuppressed?: boolean;
  clockOffsetMs?: number;
}

export interface SavedState {
  version: 1;
  progress: Record<string, PuzzleProgress>;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const STORAGE_KEY = "whats-the-code:v1";

export function emptyState(): SavedState {
  return { version: 1, progress: {} };
}

function isProgress(value: unknown): value is PuzzleProgress {
  if (!value || typeof value !== "object") return false;
  const progress = value as Partial<PuzzleProgress>;
  return (
    typeof progress.date === "string" &&
    ["ready", "playing", "solved", "failed"].includes(progress.status ?? "") &&
    Array.isArray(progress.attempts) &&
    progress.attempts.every((attempt) => typeof attempt === "string") &&
    (progress.status === "ready" || (typeof progress.startedAt === "number" && Number.isFinite(progress.startedAt))) &&
    typeof progress.countsForStreak === "boolean"
  );
}

export function loadState(storage: StorageLike): SavedState {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return emptyState();
    const state = parsed as Partial<SavedState>;
    if (state.version !== 1 || !state.progress || typeof state.progress !== "object") {
      return emptyState();
    }

    const progress = Object.fromEntries(
      Object.entries(state.progress).filter((entry): entry is [string, PuzzleProgress] =>
        isProgress(entry[1]),
      ).map(([date, value]): [string, PuzzleProgress] => {
        const migrated: PuzzleProgress = value.timingVersion !== 2 && value.status === "playing" && value.attempts.length === 0
          ? { date, status: "ready", attempts: [], countsForStreak: false, timingVersion: 2 }
          : value;
        return [date, {
          ...migrated,
          maxAttempts: migrated.status === "playing" || migrated.status === "ready"
            ? APP_CONFIG.maxAttempts
            : migrated.maxAttempts ?? LEGACY_MAX_ATTEMPTS,
        }];
      }),
    );
    return { version: 1, progress };
  } catch {
    return emptyState();
  }
}

export function saveState(storage: StorageLike, state: SavedState): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // A private or full browser store must not prevent play.
  }
}

export function sameProgress(left: PuzzleProgress | undefined, right: PuzzleProgress): boolean {
  if (!left) return false;
  return left.date === right.date && left.status === right.status &&
    left.startedAt === right.startedAt && left.completedAt === right.completedAt &&
    left.solveTimeMs === right.solveTimeMs && left.runId === right.runId &&
    left.ranked === right.ranked && left.leaderboardName === right.leaderboardName &&
    left.countsForStreak === right.countsForStreak && left.timingVersion === right.timingVersion &&
    left.maxAttempts === right.maxAttempts &&
    left.leaderboardAutoJoinSuppressed === right.leaderboardAutoJoinSuppressed &&
    left.attempts.join(",") === right.attempts.join(",");
}

export function saveProgress(storage: StorageLike, progress: PuzzleProgress): SavedState {
  const state = loadState(storage);
  const changed = !sameProgress(state.progress[progress.date], progress);
  state.progress[progress.date] = progress;
  if (changed) saveState(storage, state);
  return state;
}

export function progressForDate(
  state: SavedState,
  date: string,
  today: string,
): PuzzleProgress {
  const existing = state.progress[date];
  if (existing) return existing;

  const created: PuzzleProgress = {
    date,
    status: "ready",
    attempts: [],
    maxAttempts: APP_CONFIG.maxAttempts,
    countsForStreak: date === today,
    timingVersion: 2,
  };
  state.progress[date] = created;
  return created;
}

export function beginPuzzle(progress: PuzzleProgress, today: string, now = Date.now()): PuzzleProgress {
  if (progress.status !== "ready") return progress;
  return {
    ...progress,
    status: "playing",
    startedAt: now,
    countsForStreak: progress.date === today,
    timingVersion: 2,
  };
}

export function elapsedTime(progress: PuzzleProgress, now = Date.now()): number {
  if (progress.startedAt === undefined || progress.status === "ready") return 0;
  return progress.solveTimeMs ?? Math.max(0, now - progress.startedAt);
}

export function submitAttempt(
  progress: PuzzleProgress,
  guess: string,
  solution: string,
  now = Date.now(),
): PuzzleProgress {
  if (progress.status !== "playing" || progress.startedAt === undefined || progress.attempts.includes(guess)) return progress;

  const attempts = [...progress.attempts, guess];
  const solved = guess === solution;
  const failed = !solved && attempts.length >= attemptLimit(progress);

  return {
    ...progress,
    attempts,
    status: solved ? "solved" : failed ? "failed" : "playing",
    ...(solved || failed
      ? {
          completedAt: now,
          solveTimeMs: Math.max(0, now - progress.startedAt),
        }
      : {}),
  };
}

export function attemptLimit(progress: PuzzleProgress): number {
  return progress.maxAttempts ?? APP_CONFIG.maxAttempts;
}

function previousUtcDate(date: string): string {
  return new Date(Date.parse(`${date}T00:00:00.000Z`) - 86_400_000).toISOString().slice(0, 10);
}

export function calculateStreak(state: SavedState, today: string): number {
  const todayProgress = state.progress[today];
  if (todayProgress?.countsForStreak && todayProgress.status === "failed") return 0;

  let cursor =
    todayProgress?.countsForStreak && todayProgress.status === "solved"
      ? today
      : previousUtcDate(today);
  let streak = 0;

  while (true) {
    const progress = state.progress[cursor];
    if (!progress?.countsForStreak || progress.status !== "solved") return streak;
    streak += 1;
    cursor = previousUtcDate(cursor);
  }
}

export function formatElapsed(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}